import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, IconButton, Typography } from '@material-ui/core';
import { BrokenImage, Close, GetApp, InsertDriveFile, RotateRight, RotateLeft } from '@material-ui/icons';
import classNames from 'classnames';
import Lightbox from 'react-image-lightbox';

// api & utils
import { getImagesInfo, getImageInfo } from 'api';
import { compressImage } from 'helper';

// types
import { IFileInfo } from 'interfaces';

// icons
import AddPhotoIcon from 'assets/icons/svg/AddPhoto';
import PdfIcon from 'assets/icons/svg/PdfIcon';
import VideoFileIcon from 'assets/icons/svg/VideoFileIcon';
import WordFileIcon from 'assets/icons/svg/WordFileIcon';
import ExcelFileIcon from 'assets/icons/svg/ExcelFileIcon';

import 'pure-react-carousel/dist/react-carousel.es.css';
import 'react-image-lightbox/style.css';
import './styles.scss';

/**
 * ---------------------------------------------------------------------------
 * Helpers
 * ---------------------------------------------------------------------------
 */

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi'];
const PDF_EXTENSIONS = ['pdf'];
const WORD_EXTENSIONS = ['doc', 'docx'];
const EXCEL_EXTENSIONS = ['xls', 'xlsx'];

function extractRelativePath(url: string) {
  const isAbsoluteUrl = url.startsWith('http') || url.startsWith('https');

  if (isAbsoluteUrl) {
    const match = url.match(/biservice\/(.+?)(\?|$)/);
    return match ? match[1] : null;
  } else return url;
}

const getExtension = (filename: string): string => {
  const finalPath = extractRelativePath(filename);

  // eslint-disable-next-line no-useless-escape
  const [, extWithQuery = ''] = (finalPath || '').split(/\.(?=[^\.]+$)/);
  return extWithQuery.split('?')[0].toLowerCase();
};

const isImage = (filename: string) => IMAGE_EXTENSIONS.includes(getExtension(filename));
const isVideo = (filename: string) => VIDEO_EXTENSIONS.includes(getExtension(filename));

const getPreviewUrl = (file: File | RemoteFile): string =>
  'downloadUrl' in file ? file.downloadUrl : URL.createObjectURL(file);

/**
 * ---------------------------------------------------------------------------
 * Types
 * ---------------------------------------------------------------------------
 */

interface RemoteFile extends IFileInfo {
  /** имя из props.files ("key" ссылки на бекенде) */
  name: string;
}

export interface FilesListProps {
  /** callback отдает массив оригинальных File либо string (id в хранилище) */
  onChange?(files: Array<File | string>): void;
  /** текущий список файлов */
  files: Array<File | string>;
  /** размеры превью */
  width?: number;
  height?: number;
  /** может ли пользователь добавлять / удалять файлы */
  editable: boolean;
  /** placeholder для инпута загрузки */
  placeholder?: React.ReactNode;
  /** скрыть инпут загрузки */
  hideInput?: boolean;
  /** превью‑кружки */
  circle?: boolean;
  /** кастомные стили контейнера */
  style?: React.CSSProperties;
  /** accept для input[type="file"] */
  accept?: string;
  /** форматы, обязательные для загрузки */
  requiredFileFormats?: string[];
  /** иконка на кнопке добавления */
  icon?: React.ReactNode;
  /** можно выбрать более одного файла (по‑умолчанию true) */
  isLimitFiles?: boolean;
  /** true — выбранные файлы ещё сжимаются и не отданы в onChange, false — отданы или сжатие упало */
  onBusyChange?(busy: boolean): void;
}

interface FilesState {
  pending: boolean;
  files: Array<File | RemoteFile>;
  lightBoxOpened: boolean;
  photoIndex: number;
  rotationAngle: number;
}

/**
 * ---------------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------------------
 */

const FilesList: React.FC<FilesListProps> = ({
  onChange,
  files: inputFiles,
  width = 50,
  height = 50,
  editable,
  placeholder = (
    <>
      Добавьте файлы <br /> (не более 20Мб)
    </>
  ),
  hideInput = false,
  circle = false,
  style,
  accept,
  requiredFileFormats = [],
  icon,
  isLimitFiles = true,
  onBusyChange,
}) => {
  /**
   * STATE
   */
  const [state, setState] = useState<FilesState>(() => ({
    pending: !!inputFiles.length && inputFiles.some((f) => typeof f === 'string'),
    files: [],
    lightBoxOpened: false,
    photoIndex: 0,
    rotationAngle: 0,
  }));
  const [extensionError, setExtensionError] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Превью, которые не загрузились: ключ есть, а объекта в бакете нет - показываем иконку, а не битую картинку.
  const [brokenPreviews, setBrokenPreviews] = useState<Set<string>>(() => new Set());

  /**
   * EFFECT: resolve remote files
   */
  useEffect(() => {
    let cancelled = false;

    const resolveFiles = async () => {
      const remoteNames = inputFiles
        .filter((f): f is string => typeof f === 'string')
        .map(extractRelativePath)
        .filter(Boolean) as string[];
      const localFiles = inputFiles.filter((f): f is File => f instanceof File);

      try {
        const remoteInfos: RemoteFile[] = remoteNames.length
          ? (await getImagesInfo(remoteNames)).map((info, idx) => ({ ...info, name: remoteNames[idx] }))
          : [];

        !cancelled &&
          setState((prev) => ({
            ...prev,
            pending: false,
            files: [...remoteInfos, ...localFiles],
          }));
      } catch {
        !cancelled &&
          setState((prev) => ({
            ...prev,
            pending: false,
            files: [...localFiles], // показываем хотя бы локальные файлы
          }));
      }
    };

    resolveFiles();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(inputFiles)]);

  /**
   * MEMO: превьюшки для lightbox
   */
  const previewUrls = useMemo(() => state.files.map(getPreviewUrl), [state.files]);

  /**
   * ---------------------------------------------------------------------
   * Handlers
   * ---------------------------------------------------------------------
   */

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    (e.target as HTMLInputElement).value = '';
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    setExtensionError(false);
    const selectedFiles = Array.from(e.target.files);

    // validate extensions (если переданы ограничения)
    const validExt = requiredFileFormats.length
      ? selectedFiles.every((f) => requiredFileFormats.includes(getExtension(f.name)))
      : true;

    if (!validExt) {
      setExtensionError(true);
      return;
    }

    setUploading(true);
    onBusyChange?.(true);
    try {
      const compressed = await Promise.all(selectedFiles.map(compressImage));

      const current = state.files.map((f) => ('downloadUrl' in f ? f.name : f));
      onChange?.([...current, ...compressed]);
    } finally {
      setUploading(false);
      onBusyChange?.(false);
    }
  };

  const handleDelete = (idx: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = state.files.filter((_, i) => i !== idx).map((f) => ('downloadUrl' in f ? f.name : f));
    onChange?.(updated);
  };

  const handlePreviewClick = (idx: number) => () => {
    const fileRef = inputFiles[idx];

    // для не‑медиа сразу скачиваем
    if (typeof fileRef === 'string' && ![...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS].includes(getExtension(fileRef))) {
      return getImageInfo(fileRef)
        .then(([{ downloadUrl }]) => window.open(downloadUrl, '_blank'))
        .catch(() => alert('Не удалось скачать файл'));
    }

    setState((prev) => ({ ...prev, lightBoxOpened: true, photoIndex: idx }));
  };

  const rotate = (direction: 'left' | 'right') => () => {
    setState((prev) => {
      const delta = direction === 'left' ? 90 : -90;
      const angle = (prev.rotationAngle + 360 + delta) % 360;
      const imgEl = document.querySelector<HTMLImageElement>('.ril-image-current');
      if (imgEl) imgEl.style.transform = `rotate(${angle}deg)`;
      return { ...prev, rotationAngle: angle };
    });
  };

  const downloadVideo = () => {
    const current = inputFiles[state.photoIndex];
    if (typeof current === 'string') {
      getImageInfo(current)
        .then(([{ downloadUrl }]) => window.open(downloadUrl, '_blank'))
        .catch(() => alert('Не удалось скачать файл'));
    }
  };

  /**
   * lightbox toolbar
   */
  const lightboxActions = () => {
    if (!state.files.length) return [];

    const current = state.files[state.photoIndex];
    const ext = getExtension(current.name);

    if (IMAGE_EXTENSIONS.includes(ext)) {
      return [
        <i key="rotateL" className="material-rotate-icons" onClick={rotate('left')}>
          <RotateLeft />
        </i>,
        <i key="rotateR" className="material-rotate-icons" onClick={rotate('right')}>
          <RotateRight />
        </i>,
      ];
    }

    if (VIDEO_EXTENSIONS.includes(ext) && 'downloadUrl' in current) {
      return [
        <i key="download" className="material-rotate-icons" onClick={downloadVideo}>
          <GetApp />
        </i>,
      ];
    }
    return [];
  };

  const renderPreview = (filename: string, url: string) => {
    if (!isImage(filename)) return renderIconByExt(filename);
    if (brokenPreviews.has(url)) return <BrokenImage color="disabled" titleAccess="Фото недоступно" />;
    // lazy: в реестрах превью - это оригинал, ужатый CSS; грузим только видимые строки.
    return (
      <img
        src={url}
        alt="preview"
        loading="lazy"
        decoding="async"
        onError={() => setBrokenPreviews((prev) => new Set(prev).add(url))}
        className={classNames('files-list__image', { 'files-list__image--circle': circle })}
      />
    );
  };

  const renderIconByExt = (filename: string) => {
    const ext = getExtension(filename);
    if (PDF_EXTENSIONS.includes(ext)) return <PdfIcon />;
    if (VIDEO_EXTENSIONS.includes(ext)) return <VideoFileIcon />;
    if (WORD_EXTENSIONS.includes(ext)) return <WordFileIcon />;
    if (EXCEL_EXTENSIONS.includes(ext)) return <ExcelFileIcon />;
    return <InsertDriveFile color="disabled" />;
  };

  const renderUploadMessage = (isError?: boolean) => (
    <Typography
      component="label"
      variant="caption"
      className={classNames('files-list__box', {
        'files-list__box--error': isError,
        'files-list__box--text': !isError,
      })}
      style={{ backgroundColor: '#fff', height }}
    >
      {isError ? `Требуется документ в формате: ${requiredFileFormats.join(', ')}` : placeholder}
    </Typography>
  );

  const renderLightboxFallback = () => {
    const current = state.files[state.photoIndex];
    if (isVideo(current.name)) {
      const src = 'downloadUrl' in current ? current.downloadUrl : URL.createObjectURL(current);
      return (
        <video controls style={{ zIndex: 9 }} height="70%">
          <source src={src} />
        </video>
      );
    }
    return 'Файл не доступен для просмотра.';
  };

  /**
   * ---------------------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------------------
   */

  return (
    <div className="files-list" style={style}>
      {!hideInput && (
        <div className="files-list__item" style={{ display: 'flex', alignItems: 'center' }}>
          <label
            className="files-list__box"
            style={{ width, height, cursor: 'pointer', borderRadius: circle ? '50%' : 7 }}
          >
            {icon ?? <AddPhotoIcon style={{ width: 32, height: 32 }} />}
            <input
              type="file"
              multiple={isLimitFiles}
              accept={accept}
              disabled={!editable}
              onClick={handleInputClick}
              onChange={handleFileInput}
            />
          </label>

          {(uploading || state.pending) && (
            <CircularProgress size={30} style={{ top: 0, transform: 'none', position: 'initial' }} />
          )}

          {((state.files.length === 0 && editable) || extensionError) && renderUploadMessage(extensionError)}
        </div>
      )}

      {state.files.length > 0 && (
        <>
          {state.files.map((fileItem, idx) => {
            const url = previewUrls[idx];
            return (
              <div key={`${fileItem.name}-${idx}`} className="files-list__item">
                <div
                  onClick={handlePreviewClick(idx)}
                  style={{ width, height }}
                  className={classNames('files-list__box', { 'files-list__box--circle': circle })}
                >
                  {renderPreview(fileItem.name, url)}

                  {editable && (
                    <IconButton className="files-list__button-remove" onClick={handleDelete(idx)}>
                      <Close />
                    </IconButton>
                  )}
                </div>
              </div>
            );
          })}

          {state.lightBoxOpened && (
            <Lightbox
              toolbarButtons={lightboxActions()}
              mainSrc={previewUrls[state.photoIndex]}
              nextSrc={previewUrls[(state.photoIndex + 1) % previewUrls.length]}
              prevSrc={previewUrls[(state.photoIndex + previewUrls.length - 1) % previewUrls.length]}
              onCloseRequest={() => setState((prev) => ({ ...prev, lightBoxOpened: false }))}
              onMovePrevRequest={() =>
                setState((prev) => ({
                  ...prev,
                  photoIndex: (prev.photoIndex + previewUrls.length - 1) % previewUrls.length,
                  rotationAngle: 0,
                }))
              }
              onMoveNextRequest={() =>
                setState((prev) => ({
                  ...prev,
                  photoIndex: (prev.photoIndex + 1) % previewUrls.length,
                  rotationAngle: 0,
                }))
              }
              imageLoadErrorMessage={renderLightboxFallback()}
            />
          )}
        </>
      )}
    </div>
  );
};

export default FilesList;
